import React, { Component } from 'react';
import {
  Backdrop,
  CircularProgress,
  Grid,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Typography,
} from '@material-ui/core';
// lodash is already included in Create React App
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import DateLocationSelector from '../components/Booking/DateLocationSelector';
import StudioForm from '../components/Booking/StudioForm';
import EmployeeInfoTable from '../components/EmployeeInfoTable';
import {
  fetchAvailabilities,
  fetchLocations,
  fetchPositionsAll,
  saveBookings,
  getSuggestedEmployees as getSuggestedRequest
} from '../helpers/externalRequests';
import i18n from '../i18n';
import '../assets/stylesheets/BookingView.css';

import {
  TYPE_STUDIO,
  TYPE_PREP,
  TYPE_SET,
  TYPE_COPY,
  TYPE_TRANSLATION_FRENCH,
  TYPE_TRANSLATION_JAPANESE,
  TYPE_TRANSLATION_CHINESE,
  TYPE_RETOUCH,
  TYPE_MERCHANDISE_COORDINATION,
  TYPE_PLANNING_PRODUCING,
  TYPE_ACC_PREP,
  TYPE_COPY_PREP,
  TYPE_INVENTORY_PREP,
  TYPE_POIRIER_SET,
  TYPE_ASSISTANT_TEAM,
  numberOfSetSupportedLocations,
} from '../constants/booking';

import {
  formatDateOnlyStringFromLocalTime,
  calculateAge,
  determineEmployeeType,
  formatEmployeeDisplayName,
  getItemCountFromLocation,
} from '../helpers/helpers';

import {
  isAvailableForSetNumber,
  generateGranularOptions,
} from '../helpers/bookingAvailabilityHelpers'

import {
  AD_LABEL,
  locationAssignmentMap,
  RETOUCH_RTW_MULTIPLIER,
  RETOUCH_MFL_MULTIPLIER,
  RETOUCH_OMACC_MULTIPLIER,
  FREELANCE,
  FULLTIME,
  CONTRACT,
} from '../constants/general';

import {
  getDoubleBookingLabel
} from '../helpers/locationHelpers';

import BookingFormBasic from '../components/Booking/BookingFormBasic';

class BookingView extends Component {
  constructor(props) {
    super(props);
    this.employees = {};
    this.state = {
      employees: null,
      includedEmployeeTypes: {
        [FULLTIME]: true,
        [CONTRACT]: true,
        [FREELANCE]: true,
      },
      openBackdrop: false,
      positionMap: {}, // positionMap: {<positionName>: {id: 1, employees: [1,2,3], attributeControls: {...}}}
      selectedLocationOption: { label: '' },
      selectedPositionName: null,
      selectedLocationNumberOfSet: AD_LABEL,
      selectedDate: new Date(),
      selectedEmployees: {},
      hasFetchedData: false,
      snackOpen: false,
      snackbarMessage: null,
      showDialog: false,
    };
  }

  checkExistingEmployees = () => {
    const { selectedEmployees } = this.state;
    if (
      Object.values(selectedEmployees).every((employees) =>
        _.isEmpty(employees)
      )
    ) {
      this.getSuggestedEmployees();
    } else {
      this.setState({
        showDialog: true,
      });
    }
  };

  getSuggestedEmployees = () => {
    this.displaySnackbar(i18n.t('booking:fetchingsuggestedEmployees'));
    const {
      selectedDate,
      includedEmployeeTypes: { fullTime, contractor, freelance },
      allLocations,
      allPositions,
      employees,
    } = this.state;

    return getSuggestedRequest({
      date: formatDateOnlyStringFromLocalTime(selectedDate),
      includeFullTime: fullTime,
      includeContractor: contractor,
      includeFreelance: freelance,
    })
      .then((data) => {
        const {
          data: { suggestedEmployees },
        } = data;
        const hasWarnings = data && data.data && data.data.warnings;
        const unfilledPositions = hasWarnings
        ? data.data.warnings.unfilledPositions.map(
          (unfilled) => {
            // prettier-ignore
            return {
            location: allLocations.find(location => location.id === unfilled.locationId).locationName,
            position: allPositions.find(position => position.id === unfilled.positionId).positionName,
            abilityTier: unfilled.requestedTier
          }
          }
        )
        : [];
        const lowAbilityTier = hasWarnings
        ? data.data.warnings.lowAbilityTier.map((low) => {
          // prettier-ignore
          return {
            location: allLocations.find(location => location.id === low.locationId).locationName,
            position: allPositions.find(position => position.id === low.positionId).positionName,
            employee: employees[low.employeeId].nickName ?
              employees[low.employeeId].nickName :
              `${employees[low.employeeId].firstName} ${employees[low.employeeId].lastName}`,
            requestedAbilityTier: low.requestedTier,
            actualAbilityTier: low.actualTier
          }
        })
        : [];
        const unsetProductTier = hasWarnings
        ? data.data.warnings.unsetProductTier.map(
          (unset) => {
            return {
              location: allLocations.find(
                (location) => location.id === unset.locationId
              ).locationName,
            };
          }
        )
        : [];
        if (_.isEmpty(data.data.suggestedEmployees)) {
          this.setState({
            unfilledPositions,
            lowAbilityTier,
            unsetProductTier,
            snackOpen: true,
            snackbarMessage: i18n.t('booking:noSuggestedEmployeesFound'),
          });
        } else {
          this.setState((oldState) => {
            const selectedEmployees = JSON.parse(
              JSON.stringify(oldState.selectedEmployees)
            ); // Need to clone state to avoid modifying it
            Object.keys(selectedEmployees).forEach((locationId) => {
              if (suggestedEmployees[locationId]) {
                // There are suggestions for this location
                selectedEmployees[locationId] = suggestedEmployees[locationId].map(employeeId => this.employees[employeeId]);
              } else {
                // There are no suggestions for this location
                selectedEmployees[locationId] = {};
              }
            });
            return {
              selectedEmployees,
              unfilledPositions,
              lowAbilityTier,
              unsetProductTier,
            };
          });
        }
      });
  };

  getMatchingAssignmentAttributeValue = (location, dailyAssignmentKey, date, attribute) => {
    if (location[dailyAssignmentKey].length) {
      const match = location[dailyAssignmentKey].find(
        (dailyAssignment) => dailyAssignment.date === date
      );
      if (match) {
        return match[attribute];
      }
    }
    return false;
  };

  buildLocationOptions = (allLocations, date) => {
    const options = [];
    if (typeof allLocations === typeof []){
      allLocations.forEach((location) => {
        let allowsDoubleBooking = false;
        let numberOfSet = AD_LABEL;
        const { locationTypeName } = location.locationType;
        // STUDIO, SET, ACCESSORY PREP and PREP are the only location types that support doubleBooking and variable number of set
        if (
          locationTypeName === 'STUDIO' ||
          locationTypeName === 'SET' ||
          locationTypeName === 'PREP' ||
          locationTypeName === 'ACCESSORY PREP'
        ) {
          const doubleBookingAttribute = locationTypeName === 'STUDIO' ? 'isDoubleHmu' : 'isDoubleStylist';
          allowsDoubleBooking = this.getMatchingAssignmentAttributeValue(location, locationAssignmentMap[locationTypeName], date, doubleBookingAttribute);
          numberOfSet = this.getMatchingAssignmentAttributeValue(location, locationAssignmentMap[locationTypeName], date, 'numberOfSet') || AD_LABEL;
        }
        options.push({
          label: location.locationName,
          value: location.id,
          type: location.locationTypeId,
          allowsDoubleBooking,
          bookingOptions: location.bookingOptions,
          numberOfSet,
          requiredPositions: location.locationType.requiredPositions
        });
      });
    }
    
    return options;
  };

  formatEmployee = (employee) => {
    const formattedEmployee = JSON.parse(JSON.stringify(employee));

    formattedEmployee.name = formatEmployeeDisplayName(
      employee.firstName,
      employee.lastName,
      employee.nickName
    );

    formattedEmployee.employeeType = determineEmployeeType(
      employee.isFullTime,
      employee.isContractor
    );

    formattedEmployee.primaryPosition = employee.primaryPositionId
      ? employee.positions.find(
          (position) => position.id === employee.primaryPositionId
        ).positionName
      : null;

    formattedEmployee.positions = {};

    employee.positions.forEach((position) => {
      formattedEmployee.positions[position.id] = position;

      if ('birthday' in position.StudioEmployee_Position.extras) {
        // eslint-disable-next-line no-param-reassign
        position.StudioEmployee_Position.extras.age = calculateAge(
          position.StudioEmployee_Position.extras.birthday
        );
        // eslint-disable-next-line no-param-reassign
        delete position.StudioEmployee_Position.extras.birthday;
      }

      if (position.id === employee.primaryPositionId) {
        formattedEmployee.abilityTier =
          formattedEmployee.positions[
            position.id
          ].StudioEmployee_Position.abilityTier;
      }
    });

    return formattedEmployee;
  };

  formatAllLocationAssignments = (allLocationsData) => {
    const formattedAssignmentData = [];
    allLocationsData.forEach((location) => {
      formattedAssignmentData[location.id] = { ...location };
    });
    return formattedAssignmentData;
  };

  fetchAllData = async (date) => {
    const dateString = formatDateOnlyStringFromLocalTime(date);
    try {
      const [schedule, allLocations, allPositions] = await Promise.all(
        [
          fetchAvailabilities(dateString),
          fetchLocations({ date: dateString, isActive: true }),
          fetchPositionsAll(),
        ]
      );
      const positionMap = {};
      this.employees = {};

      allPositions.data.forEach((position) => {
        positionMap[position.positionName] = {};
        positionMap[position.positionName].id = position.id;
        positionMap[position.positionName].employees = [];
        positionMap[position.positionName].attributeControls =
          position.attributeControls;
      });

      const allAvailabilities = schedule.data.data;
      allAvailabilities.forEach((availability) => {
        const formattedEmployee = this.formatEmployee(
          availability.studioEmployee
        );
        formattedEmployee.availabilityType = availability.availabilityType;
        this.employees[formattedEmployee.id] = formattedEmployee;

        Object.values(formattedEmployee.positions).forEach((position) => {
          positionMap[position.positionName].employees.push(
            formattedEmployee.id
          );
        });
      });

      const selectedEmployees = {};
      allLocations.data.forEach((location) => {
        selectedEmployees[location.id] = {};
      });

      const allLocationOptions = this.buildLocationOptions(
        allLocations.data,
        dateString
      );
      const selectedLocationOption = allLocationOptions[0];

      const allLocationAssignments = this.formatAllLocationAssignments(
        allLocations.data
      );

      const employees = JSON.parse(JSON.stringify(this.employees));

      this.setState({
        employees,
        positionMap,
        allLocationAssignments,
        allLocationOptions,
        selectedLocationOption,
        selectedEmployees,
        hasFetchedData: true,
        unfilledPositions: [],
        lowAbilityTier: [],
        unsetProductTier: [],
        allLocations: allLocations.data,
        allPositions: allPositions.data,
      });
    } catch (error) {
      this.displaySnackbar(`${i18n.t('booking:fetchError')} ${error}`);
    }
  };

  componentDidMount = () => {
    const { selectedDate: date } = this.state;
    this.fetchAllData(date);
  };

  onLocationChange = (locationOption) => {
    this.setState({
      selectedLocationOption: locationOption,
      selectedPositionName: null,
    });
  };

  onFormEmployeeSelect = (position, employees) => {
    this.setState((prevState) => {
      const locationId = prevState.selectedLocationOption.value;
      const newSelectedEmployees = JSON.parse(
        JSON.stringify(prevState.selectedEmployees)
      );
      if (employees === null) {
        newSelectedEmployees[locationId][position] = [];
      } else {
        newSelectedEmployees[locationId][position] = employees;
      }
      return { selectedEmployees: newSelectedEmployees };
    });
  };

  showEmployeesOfPosition = (position, numberOfSet) => {
    const selectedLocationNumberOfSet = numberOfSet || AD_LABEL;
    this.setState({
      selectedLocationNumberOfSet,
      selectedPositionName: position,
    });
  };

  isNotSelectedForAnotherPosition = (
    employeeId,
    selectedEmployees,
    selectedLocationId,
    positionName
  ) => {
    let isNotSelected = true;
    Object.keys(selectedEmployees[selectedLocationId]).forEach(
      (positionNameOfSelectedLocation) => {
        if (
          positionNameOfSelectedLocation !== positionName &&
          selectedEmployees[selectedLocationId][
            positionNameOfSelectedLocation
          ].map(employee => employee.id).includes(employeeId)
        ) {
          isNotSelected = false;
        }
      }
    );
    return isNotSelected;
  };

  buildOptions = (positionName, numberOfSet, granularOptions) => {
    const {
      employees,
      positionMap,
      selectedLocationOption,
      selectedEmployees,
    } = this.state;
    const selectedLocationId = selectedLocationOption.value;

    const options = [];

    if (positionName in positionMap) {
      positionMap[positionName].employees.forEach((employeeId) => {
        const employee = employees[employeeId];
        // we only add the employee if their availability is compatible with the number of set and they are not already selected somewhere else
        if (
          isAvailableForSetNumber(
            employee.availabilityType,
            numberOfSet
          ) && 
          selectedEmployees[selectedLocationId]
          && this.isNotSelectedForAnotherPosition(employeeId, selectedEmployees, selectedLocationId, positionName)
        ) {
          const option = {
            id: employee.id,
            label: employee.name,
            value: employee.id,
            availabilityType: employee.availabilityType,
            employeeAvailabilityId: employee.employeeAvailabilityId,
          };
          if (selectedEmployees[selectedLocationId][positionName]) {
            option.isSelected = selectedEmployees[selectedLocationId][
              positionName
            ].map(selectedEmployee => selectedEmployee.id).includes(employeeId);
          } else {
            option.isSelected = false;
          }
          if (granularOptions) {
            options.push({
              label: employee.name,
              options: generateGranularOptions(option, selectedEmployees[selectedLocationId][positionName]),
            });
            return;
          }
          options.push(option);
        }
      });
    }

    return options;
  };

  buildRequiredPositions = (requiredPositions, gender, numberOfSet, granularOptions) => {
    let lastGroup = null;
    const sorted = requiredPositions.sort((a,b)=> a.group === b.group ? a.order-b.order : a.group-b.group);
    return sorted.reduce((res, requiredPosition) => {
      if (!requiredPosition.enabled || (requiredPosition.gender && requiredPosition.gender !== gender)) {
        return res;
      }
      if (lastGroup && lastGroup !== requiredPosition.group) {
        res.push({
          divider: true,
        });
      }
      res.push({
        eligibleEmployees: this.buildOptions(requiredPosition.position.positionName, numberOfSet, granularOptions),
        positionTitle: requiredPosition.position.positionName,
        positionLabel: i18n.t(`booking:${requiredPosition.position.i18nLabel}`)
      })
      lastGroup = requiredPosition.group
      return res;
    }, [])
  }

  executeSave = async () => {
    const {
      selectedLocationOption,
      positionMap,
      selectedEmployees,
      selectedDate,
    } = this.state;

    const employeesToBook = [];
    const locationId = selectedLocationOption.value;
    let { allowsDoubleBooking } = selectedLocationOption;
    const { type, numberOfSet } = selectedLocationOption;
    Object.keys(selectedEmployees[locationId]).forEach((positionName) => {
      const positionId = positionMap[positionName].id;
      const selectedEmployeesOfPosition =
        selectedEmployees[locationId][positionName];

      // Checking if type is studio and position is stylist
      if (
        type === TYPE_STUDIO &&
        positionName.toLowerCase().includes('stylist')
      ) {
        allowsDoubleBooking = false;
      }

      selectedEmployeesOfPosition.forEach((employee) => {
        employeesToBook.push({
          positionId,
          locationId,
          studioEmployeeId: employee.id,
          availabilityType: numberOfSetSupportedLocations.includes(type) ? numberOfSet : employee.availabilityType,
          allowsDoubleBooking,
        });
      });
    });

    const date = formatDateOnlyStringFromLocalTime(selectedDate);
    try {
      await saveBookings(date, employeesToBook);
      // reset forms, update booking data
      const { selectedDate: selctedDateState } = this.state;
      await this.fetchAllData(selctedDateState);
      this.setState({openBackdrop: false})
      this.displaySnackbar(i18n.t('booking:saveSuccess'));
    } catch {
      this.setState({ openBackdrop: false });
      this.displaySnackbar(i18n.t('booking:errorError'))
    }
  }

  handleSave = () => {
    this.setState({ openBackdrop: true }, () => this.executeSave());
  };

  displaySnackbar = (message) => {
    this.setState({
      snackOpen: true,
      snackbarMessage: message,
    });
  };

  hideSnackbar = () => {
    this.setState({ snackOpen: false });
  };

  renderForm = () => {
    const {
      hasFetchedData,
      allLocationAssignments,
      selectedDate,
      selectedLocationOption: { label, type, value, allowsDoubleBooking, bookingOptions, requiredPositions },
    } = this.state;

    const formattedDate = formatDateOnlyStringFromLocalTime(selectedDate);

    if (!hasFetchedData) {
      return <CircularProgress />;
    }

    const locationId = value;

    const studioLocationAssignment = allLocationAssignments[
      locationId
    ].studiodailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const accessoryLocationAssignment = allLocationAssignments[
      locationId
    ].accessorydailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const prepLocationAssignment = allLocationAssignments[
      locationId
    ].prepdailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const copyLocationAssignment = allLocationAssignments[
      locationId
    ].copydailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const copyPrepLocationAssignment = allLocationAssignments[
      locationId
    ].copyprepdailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const translationLocationAssignment = allLocationAssignments[
      locationId
    ].translationdailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const retouchLocationAssignment = allLocationAssignments[
      locationId
    ].retouchdailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const accessoryPrepLocationAssignment = allLocationAssignments[
      locationId
    ].accessoryprepdailyassignments.find(
      (assignment) => assignment.date === formattedDate
    );
    const gender = (() => {
      if (studioLocationAssignment) return studioLocationAssignment.gender
      if (prepLocationAssignment) return prepLocationAssignment.gender
      return 'male'
    })();
    const numberOfSetStudio = studioLocationAssignment
      ? studioLocationAssignment.numberOfSet
      : AD_LABEL;
    const numberOfSetAcc = accessoryLocationAssignment
      ? accessoryLocationAssignment.numberOfSet
      : AD_LABEL;
    const numberOfSetPrep = prepLocationAssignment
      ? prepLocationAssignment.numberOfSet
      : AD_LABEL;
    const numberOfSetAccPrep = accessoryPrepLocationAssignment
      ? accessoryPrepLocationAssignment.numberOfSet
      : AD_LABEL;

    const prepItems = getItemCountFromLocation(
      allLocationAssignments.filter(
        (locationAssignment) =>
          locationAssignment.locationTypeId === TYPE_PREP
      ),
      formattedDate,
      'prepdailyassignments',
      [
        { key: 'rtwCount', multiplier: 1 },
        { key: 'lingerieCount', multiplier: 1 }
      ]
    );

    const translationItems = getItemCountFromLocation(
      allLocationAssignments.filter(
        (locationAssignment) =>
          locationAssignment.locationTypeId === TYPE_STUDIO
      ),
      formattedDate,
      'studiodailyassignments',
      [
        { key: 'rtwCount', multiplier: 1 }
      ]
    );

    const retouchItems = getItemCountFromLocation(
      allLocationAssignments.filter(
        (locationAssignment) =>
          locationAssignment.locationTypeId === TYPE_STUDIO
      ),
      formattedDate,
      'studiodailyassignments',
      [
        { key: 'rtwCount', multiplier: RETOUCH_RTW_MULTIPLIER },
        { key: 'mflCount', multiplier: RETOUCH_MFL_MULTIPLIER },
        { key: 'omaccCount', multiplier: RETOUCH_OMACC_MULTIPLIER }
      ]);

    const doubleBookingInfo = requiredPositions
      .filter((reqPos)=>reqPos.allowDoubleBooking && reqPos.enabled && (reqPos.gender === null || reqPos.gender === gender))
      .map((reqPos)=>{
        const bookingOption = bookingOptions.find((opt)=> opt.positionId === reqPos.positionId);
        return {
          label: i18n.t(getDoubleBookingLabel(reqPos.position)),
          value: bookingOption ? bookingOption.allowsDoubleBooking : false,
        }
      });

    const additionalAccSetItems = [
      {
        label: i18n.t('location:pets').toUpperCase(),
        value: accessoryLocationAssignment
          ? accessoryLocationAssignment.petsCount
          : 0,
      },
      {
        label: i18n.t('location:kids').toUpperCase(),
        value: accessoryLocationAssignment
          ? accessoryLocationAssignment.kidsCount
          : 0,
      },
      {
        label: i18n.t('location:homeTechActivityAbreviation').toUpperCase(),
        value: accessoryLocationAssignment
          ? accessoryLocationAssignment.homeTechActivityCount
          : 0,
      },
      {
        label: i18n.t('location:selfCareAbreviation').toUpperCase(),
        value: accessoryLocationAssignment
          ? accessoryLocationAssignment.selfCareCount
          : 0,
      },
    ];

    switch (type) {
      case TYPE_STUDIO:
        return (
          <StudioForm
            locationName={label}
            locationId={locationId}
            info={[
              {
                label: i18n.t('location:numberOfSet'),
                value: i18n.t(`location:${numberOfSetStudio}`),
              },
              ...doubleBookingInfo,
            ]}
            inlineInfo={{
              label: i18n.t('location:skuCount'),
              items: [
                {
                  label: i18n.t('location:rtw').toUpperCase(),
                  value: studioLocationAssignment ? studioLocationAssignment.rtwCount : 0,
                },
                {
                  label: i18n.t('location:mfl').toUpperCase(),
                  value: studioLocationAssignment ? studioLocationAssignment.mflCount : 0,
                },
                {
                  label: i18n.t('location:omacc').toUpperCase(),
                  value: studioLocationAssignment ? studioLocationAssignment.omaccCount : 0,
                },
                {
                  label: i18n.t('location:pets').toUpperCase(),
                  value: studioLocationAssignment ? studioLocationAssignment.petsCount : 0,
                },
                {
                  label: i18n.t('location:kids').toUpperCase(),
                  value: studioLocationAssignment ? studioLocationAssignment.kidsCount : 0,
                },
                {
                  label: i18n.t('location:homeTechActivityAbreviation').toUpperCase(),
                  value: studioLocationAssignment ? studioLocationAssignment.homeTechActivityCount : 0,
                },
                {
                  label: i18n.t('location:selfCareAbreviation').toUpperCase(),
                  value: studioLocationAssignment ? studioLocationAssignment.selfCareCount : 0,
                },
              ]
            }}
            gender={
              studioLocationAssignment
                ? studioLocationAssignment.gender
                : i18n.t('noData')
            }
            percentage={
              studioLocationAssignment
                ? studioLocationAssignment.percentage
                : i18n.t('noData')
            }
            numberOfSet={numberOfSetStudio}
            requiredPositions={this.buildRequiredPositions(requiredPositions,gender,numberOfSetStudio,false)}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              studioLocationAssignment
                ? studioLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              studioLocationAssignment &&
              studioLocationAssignment.specialProject
                ? studioLocationAssignment.specialProject
                : false
            }
            productTier={
              studioLocationAssignment && studioLocationAssignment.productTier
                ? studioLocationAssignment.productTier
                : 0
            }
          />
        );
      case TYPE_SET:
        return (
          <StudioForm
            locationName={label}
            locationId={locationId}
            info={[
              {
                label: i18n.t('location:numberOfSet'),
                value: i18n.t(`location:${numberOfSetAcc}`),
              },
              ...doubleBookingInfo,
            ]}
            inlineInfo={{
              label: i18n.t('location:skuCount'),
              items: [
                {
                  label: i18n.t('location:acc').toUpperCase(),
                  value: accessoryLocationAssignment
                    ? accessoryLocationAssignment.accCount
                    : 0,
                },
                {
                  label: i18n.t('location:bag').toUpperCase(),
                  value: accessoryLocationAssignment
                    ? accessoryLocationAssignment.bagCount
                    : 0,
                },
                {
                  label: i18n.t('location:shoe').toUpperCase(),
                  value: accessoryLocationAssignment
                    ? accessoryLocationAssignment.shoeCount
                    : 0,
                },
                ...additionalAccSetItems,
              ]
            }}
            numberOfSet={numberOfSetAcc}
            percentage={
              accessoryLocationAssignment
                ? accessoryLocationAssignment.percentage
                : i18n.t('noData')
            }
            setType={
              accessoryLocationAssignment
                ? accessoryLocationAssignment.typeOfSet
                : i18n.t('noData')
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions,null,numberOfSetAcc,false)}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              accessoryLocationAssignment
                ? accessoryLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              accessoryLocationAssignment
                ? accessoryLocationAssignment.specialProject
                : false
            }
            productTier={
              accessoryLocationAssignment &&
              accessoryLocationAssignment.productTier
                ? accessoryLocationAssignment.productTier
                : 0
            }
          />
        );
      case TYPE_POIRIER_SET:
        return (
          <StudioForm
            locationName={label}
            locationId={locationId}
            info={[
              {
                label: i18n.t('location:numberOfSet'),
                value: i18n.t(`location:${numberOfSetAcc}`),
              },
              ...doubleBookingInfo,
              ]}
            inlineInfo={{
              label: i18n.t('location:skuCount'),
              items: [
                  ...additionalAccSetItems,
                ]
              }}
            numberOfSet={i18n.t('noData')}
            percentage={
               i18n.t('noData')
            }
            setType={
               i18n.t('noData')
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions,null,numberOfSetAcc,true)}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              false
            }
            specialProject={
               false
            }
            productTier={
                0
            }
          />
        );
      

      case TYPE_PREP:
        return (
          <StudioForm
            locationName={label}
            locationId={locationId}
            info={[
              {
                label: i18n.t('location:numberOfSet'),
                value: i18n.t(`location:${numberOfSetPrep}`),
              },
              ...doubleBookingInfo,
            ]}
            inlineInfo={{
              label: i18n.t('location:skuCount'),
              items: [
                {
                  label: i18n.t('location:rtw').toUpperCase(),
                  value: prepLocationAssignment ? prepLocationAssignment.rtwCount : 0,
                },
                {
                  label: i18n.t('location:mfl').toUpperCase(),
                  value: prepLocationAssignment ? prepLocationAssignment.mflCount : 0,
                },
                {
                  label: i18n.t('location:omacc').toUpperCase(),
                  value: prepLocationAssignment ? prepLocationAssignment.omaccCount : 0,
                },
                {
                  label: i18n.t('location:pets').toUpperCase(),
                  value: prepLocationAssignment ? prepLocationAssignment.petsCount : 0,
                },
                {
                  label: i18n.t('location:kids').toUpperCase(),
                  value: prepLocationAssignment ? prepLocationAssignment.kidsCount : 0,
                },
                {
                  label: i18n.t('location:homeTechActivityAbreviation').toUpperCase(),
                  value: prepLocationAssignment ? prepLocationAssignment.homeTechActivityCount : 0,
                },
                {
                  label: i18n.t('location:selfCareAbreviation').toUpperCase(),
                  value: prepLocationAssignment ? prepLocationAssignment.selfCareCount : 0,
                },
              ]
            }}
            numberOfSet={numberOfSetPrep}
            gender={
              prepLocationAssignment
                ? prepLocationAssignment.gender
                : i18n.t('noData')
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions,gender,numberOfSetPrep,false)}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              prepLocationAssignment
                ? prepLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              prepLocationAssignment && prepLocationAssignment.specialProject
                ? prepLocationAssignment.specialProject
                : false
            }
            productTier={
              prepLocationAssignment && prepLocationAssignment.productTier
                ? prepLocationAssignment.productTier
                : 0
            }
          />
        );

      case TYPE_ACC_PREP:
        return (
          <StudioForm
            locationName={label}
            locationId={locationId}
            info={[
              {
                label: i18n.t('location:numberOfSet'),
                value: i18n.t(`location:${numberOfSetAccPrep}`),
              },
              ...doubleBookingInfo,
              {
                label: i18n.t('location:doubleStylist'),
                value: allowsDoubleBooking || false,
              }
            ]}
            inlineInfo={{
              label: i18n.t('location:skuCount'),
              items: [
                {
                  label: i18n.t('location:acc').toUpperCase(),
                  value: accessoryPrepLocationAssignment
                    ? accessoryPrepLocationAssignment.accCount
                    : 0,
                },
                {
                  label: i18n.t('location:bag').toUpperCase(),
                  value: accessoryPrepLocationAssignment
                    ? accessoryPrepLocationAssignment.bagCount
                    : 0,
                },
                {
                  label: i18n.t('location:shoe').toUpperCase(),
                  value: accessoryPrepLocationAssignment
                    ? accessoryPrepLocationAssignment.shoeCount
                    : 0,
                },
                {
                  label: i18n.t('location:pets').toUpperCase(),
                  value: accessoryPrepLocationAssignment
                    ? accessoryPrepLocationAssignment.petsCount
                    : 0,
                },
                {
                  label: i18n.t('location:kids').toUpperCase(),
                  value: accessoryPrepLocationAssignment
                    ? accessoryPrepLocationAssignment.kidsCount
                    : 0,
                },
                {
                  label: i18n.t('location:homeTechActivityAbreviation').toUpperCase(),
                  value: accessoryPrepLocationAssignment
                    ? accessoryPrepLocationAssignment.homeTechActivityCount
                    : 0,
                },
                {
                  label: i18n.t('location:selfCareAbreviation').toUpperCase(),
                  value: accessoryPrepLocationAssignment
                    ? accessoryPrepLocationAssignment.selfCareCount
                    : 0,
                },
              ]
            }}
            numberOfSet={numberOfSetAccPrep}
            setType={
              accessoryPrepLocationAssignment
                ? accessoryPrepLocationAssignment.typeOfSet
                : i18n.t('noData')
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, numberOfSetAccPrep, false)}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              accessoryPrepLocationAssignment
                ? accessoryPrepLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              accessoryPrepLocationAssignment
                ? accessoryPrepLocationAssignment.specialProject
                : false
            }
            productTier={
              accessoryPrepLocationAssignment &&
              accessoryPrepLocationAssignment.productTier
                ? accessoryPrepLocationAssignment.productTier
                : 0
            }
          />
        );

      case TYPE_TRANSLATION_FRENCH:
        return (
          <BookingFormBasic
            locationName={label}
            locationId={locationId}
            items={[
              {
                label: i18n.t('booking:items'),
                value: translationItems,
              },
              {
                label: i18n.t('booking:additionalItems'),
                value: translationLocationAssignment
                ? translationLocationAssignment.additionalItems
                : 0
              }
            ]}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              translationLocationAssignment
                ? translationLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              translationLocationAssignment &&
              translationLocationAssignment.specialProject
                ? translationLocationAssignment.specialProject
                : false
            }
            productTier={
              translationLocationAssignment &&
              translationLocationAssignment.productTier
                ? translationLocationAssignment.productTier
                : 0
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
          />
        );
      case TYPE_TRANSLATION_JAPANESE:
        return (
          <BookingFormBasic
            locationName={label}
            locationId={locationId}
            items={[
              {
                label: i18n.t('booking:items'),
                value: translationItems,
              },
              {
                label: i18n.t('booking:additionalItems'),
                value: translationLocationAssignment
                ? translationLocationAssignment.additionalItems
                : 0
              }
            ]}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              translationLocationAssignment
                ? translationLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              translationLocationAssignment &&
              translationLocationAssignment.specialProject
                ? translationLocationAssignment.specialProject
                : false
            }
            productTier={
              translationLocationAssignment &&
              translationLocationAssignment.productTier
                ? translationLocationAssignment.productTier
                : 0
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
          />
        );
      case TYPE_TRANSLATION_CHINESE:
        return (
          <BookingFormBasic
            locationName={label}
            locationId={locationId}
            items={[
              {
                label: i18n.t('booking:items'),
                value: translationItems,
              },
              {
                label: i18n.t('booking:additionalItems'),
                value: translationLocationAssignment
                ? translationLocationAssignment.additionalItems
                : 0
              }
            ]}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              translationLocationAssignment
                ? translationLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              translationLocationAssignment &&
              translationLocationAssignment.specialProject
                ? translationLocationAssignment.specialProject
                : false
            }
            productTier={
              translationLocationAssignment &&
              translationLocationAssignment.productTier
                ? translationLocationAssignment.productTier
                : 0
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
          />
        );
      case TYPE_COPY:
        return (
          <BookingFormBasic
            locationId={locationId}
            locationName={label}
            items={[
              { 
                label: i18n.t('booking:items'),
                value: prepItems
              },
              { 
                label: i18n.t('booking:additionalItems'), 
                value: copyLocationAssignment
                  ? copyLocationAssignment.additionalItems
                  : 0
              }
            ]}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              copyLocationAssignment
                ? copyLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              copyLocationAssignment && copyLocationAssignment.specialProject
                ? copyLocationAssignment.specialProject
                : false
            }
            productTier={
              copyLocationAssignment ? copyLocationAssignment.productTier : 0
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
          />
        );
        case TYPE_COPY_PREP:
          return (
            <BookingFormBasic
              locationId={locationId}
              locationName={label}
              items={[
                {
                  label: i18n.t('booking:stylesToReceive'),
                  value: copyPrepLocationAssignment
                    ? copyPrepLocationAssignment.stylesToReceive
                    : 0
                }
              ]}
              handleSave={this.handleSave}
              showEmployeesOfPosition={this.showEmployeesOfPosition}
              onFormEmployeeSelect={this.onFormEmployeeSelect}
              isSpecialProject={
                copyPrepLocationAssignment
                  ? copyPrepLocationAssignment.isSpecialProject
                  : false
              }
              specialProject={
                copyPrepLocationAssignment && copyPrepLocationAssignment.specialProject
                  ? copyPrepLocationAssignment.specialProject
                  : false
              }
              productTier={
                copyPrepLocationAssignment ? copyPrepLocationAssignment.productTier : 0
              }
              requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
            />
          );
      case TYPE_RETOUCH:
        return (
          <BookingFormBasic
            locationName={label}
            locationId={locationId}
            items={[
              { 
                label: i18n.t('booking:items'),
                value: retouchItems,
              },
              { 
                label: i18n.t('booking:additionalItems'), 
                value: retouchLocationAssignment
                ? retouchLocationAssignment.additionalItems
                : 0
              }
            ]}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
              retouchLocationAssignment
                ? retouchLocationAssignment.isSpecialProject
                : false
            }
            specialProject={
              retouchLocationAssignment &&
              retouchLocationAssignment.specialProject
                ? retouchLocationAssignment.specialProject
                : false
            }
            productTier={
              retouchLocationAssignment && retouchLocationAssignment.productTier
                ? retouchLocationAssignment.productTier
                : 0
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
          />
        );
      case TYPE_MERCHANDISE_COORDINATION:
        return (
          <BookingFormBasic
            locationName={label}
            locationId={locationId}
            isSpecialProject={false}
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
          />
        );
      case TYPE_PLANNING_PRODUCING:
        return (
          <BookingFormBasic
            locationName={label}
            locationId={locationId}
            isSpecialProject={false}
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
          />
        );
      case TYPE_INVENTORY_PREP:
      case TYPE_ASSISTANT_TEAM:
        return (
          <BookingFormBasic
            locationId={locationId}
            locationName={label}
            handleSave={this.handleSave}
            showEmployeesOfPosition={this.showEmployeesOfPosition}
            onFormEmployeeSelect={this.onFormEmployeeSelect}
            isSpecialProject={
               false
            }
            specialProject={
               false
            }
            requiredPositions={this.buildRequiredPositions(requiredPositions, null, AD_LABEL, true)}
          />
        );
      default:
        return <p>{i18n.t('booking:formTypeNotFound')}</p>;
    }
  };

  fetchDateSchedule = (dateToFetch) => {
    this.setState(
      {
        selectedDate: dateToFetch,
      },
    () => this.fetchAllData(dateToFetch)
    );
  };

  filterEmployees = (
    employees,
    positionMap,
    selectedPositionName,
    selectedLocationNumberOfSet
  ) => {
    if (selectedPositionName && positionMap[selectedPositionName].employees) {
      const employeeIds = positionMap[selectedPositionName].employees;
      return employeeIds
        .map((employeeId) => employees[employeeId])
        .filter((employee) =>
        isAvailableForSetNumber(
            employee.availabilityType,
            selectedLocationNumberOfSet
          )
        );
    }
    return [];
  };

  updateIncludedEmployees = () => {
    const { includedEmployeeTypes, positionMap } = this.state;
    const updatedPositionMap = JSON.parse(JSON.stringify(positionMap));
    const keys = Object.keys(this.employees);
    const employees = {};
    keys.forEach((key) => {
      if (includedEmployeeTypes[this.employees[key].employeeType]) {
        employees[key] = JSON.parse(JSON.stringify(this.employees[key]));
      }
    });
    // clear employee ids
    Object.keys(updatedPositionMap).forEach((positionKey) => {
      updatedPositionMap[positionKey].employees = [];
    });
    Object.keys(employees).forEach((employeeKey) => {
      Object.values(employees[employeeKey].positions).forEach((position) => {
        updatedPositionMap[position.positionName].employees.push(
          employees[employeeKey].id
        );
      });
    });
    this.setState({ employees, positionMap: updatedPositionMap });
  };

  includedEmployeeTypeChange = (employeeType, value) => {
    this.setState(
      (prevState) => ({
        includedEmployeeTypes: {
          ...prevState.includedEmployeeTypes,
          [employeeType]: value,
        },
      }),
      () => this.updateIncludedEmployees()
    );
  };

  closeDialog = () => {
    this.setState({
      showDialog: false,
    });
  };

  confirmOverwrite = () => {
    this.setState(
      {
        showDialog: false,
      },
      () => {
        this.getSuggestedEmployees();
      }
    );
  };

  render() {
    const {
      openBackdrop,
      selectedLocationOption,
      selectedDate,
      employees,
      includedEmployeeTypes,
      allLocationOptions,
      selectedPositionName,
      selectedLocationNumberOfSet,
      positionMap,
      selectedEmployees,
      snackOpen,
      snackbarMessage,
      unfilledPositions,
      lowAbilityTier,
      unsetProductTier,
      showDialog,
    } = this.state;

    const selectedLocationId = selectedLocationOption.value;
    const form = this.renderForm();

    let selectedEmployeeIds = [];
    if (
      selectedEmployees[selectedLocationId] &&
      selectedEmployees[selectedLocationId][selectedPositionName]
    ) {
      selectedEmployeeIds =
        selectedEmployees[selectedLocationId][selectedPositionName].map(employee => employee.id);
    }

    return (
      <div className="callsheet-container bookingView">
        <Dialog open={showDialog} onClose={this.closeDialog}>
          <DialogTitle disableTypography>
            <Typography variant="h4">
              {_.isEmpty(selectedEmployees[selectedLocationOption.value])
                ? i18n.t('booking:overwriteOtherEmployees')
                : i18n.t('booking:overwriteEmployees')}
            </Typography>
          </DialogTitle>
          <DialogActions>
            <Button onClick={this.closeDialog}>
              {i18n.t('booking:cancel')}
            </Button>
            <Button onClick={this.confirmOverwrite}>
              {i18n.t('booking:confirm')}
            </Button>
          </DialogActions>
        </Dialog>
        <Grid container>
          <Grid item xs={4}>
            {allLocationOptions && (
              <DateLocationSelector
                selectedDate={selectedDate}
                fetchDateSchedule={this.fetchDateSchedule}
                selectedLocation={selectedLocationOption}
                locations={allLocationOptions}
                onLocationChange={this.onLocationChange}
                includedEmployeeTypes={includedEmployeeTypes}
                onIncludedEmployeeTypeChange={this.includedEmployeeTypeChange}
                getSuggestedEmployees={this.checkExistingEmployees}
                unfilledPositions={unfilledPositions}
                lowAbilityTier={lowAbilityTier}
                unsetProductTier={unsetProductTier}
              />
            )}
            {employees && form}
          </Grid>
          <Grid item xs={8}>
            {employees && (
              <EmployeeInfoTable
                positionId={
                  positionMap[selectedPositionName]
                    ? positionMap[selectedPositionName].id
                    : null
                }
                attributeControls={
                  positionMap[selectedPositionName]
                    ? (positionMap[selectedPositionName].attributeControls).filter(attribute => attribute.displayInBookingTable)
                    : null
                }
                employees={this.filterEmployees(
                  employees,
                  positionMap,
                  selectedPositionName,
                  selectedLocationNumberOfSet
                )}
                selectedEmployeeIds={selectedEmployeeIds}
              />
            )}
          </Grid>
          <Snackbar
            message={snackbarMessage}
            open={snackOpen}
            onClose={this.hideSnackbar}
            autoHideDuration={2000}
          />
        </Grid>
        <Backdrop open={openBackdrop} className="backdrop">
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    );
  }
}

export default BookingView;
